import {
  Box, Button, Switch, Typography,
} from '@mui/material';
import classNames from 'classnames';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { useNextStepAfterUpdate } from '../Wizard/Wizard.hooks';

const useStyles = makeStyles()((theme) => ({
  container: {
    width: '100%',
    maxWidth: 720,
    margin: '0 auto',
    textAlign: 'center',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    rowGap: 5,
    columnGap: 4,
    alignItems: 'center',
    justifyItems: 'center',
  },
  left: {
    textAlign: 'end',
  },
  right: {
    textAlign: 'start',
  },
  label: {
    fontSize: '1.1rem',
    color: '#fff',
    transition: 'opacity .2s',
    cursor: 'pointer',
  },
  labelDimmed: {
    opacity: 0.5,
  },
  hint: {
    fontStyle: 'italic',
    opacity: 0.8,
    lineHeight: 1.4,
    maxWidth: 280,
    textAlign: 'center',
  },

  switchRoot: {
    width: 56,
    height: 32,
    padding: 0,
  },
  switchBase: {
    padding: 4,
    '&.Mui-checked': {
      transform: 'translateX(24px) !important',
    },
    '&.Mui-checked + .MuiSwitch-track': {
      backgroundColor: 'transparent',
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      boxShadow: `0 0 0 3px ${theme.palette.primary.main}`,
    },
  },
  thumb: {
    width: 24,
    height: 24,
    boxShadow: 'none',
    backgroundColor: '#fff',
  },
  track: {
    borderRadius: 16,
    backgroundColor: 'transparent !important',
    opacity: '1 !important',
    border: `2px solid ${theme.palette.primary.main} !important`,
  },

  next: {
    display: 'flex',
    justifyContent: 'end',
    marginTop: 20,
  },
}));

export interface DifficultyProps {}

const Difficulty: React.FC<DifficultyProps> = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const next = useNextStepAfterUpdate();

  const [isComplex, setIsComplex] = React.useState(true);

  const switchId = 'difficulty-switch';
  const simpleId = 'simple';
  const complexId = 'complex';
  const hintSimpleId = 'hint-simple';
  const hintComplexId = 'hint-complex';

  const handleToggle = () => {
    setIsComplex((v) => !v);
  };

  const handleNext = () => {
    next({ difficulty: isComplex ? 'full' : 'simple' });
  };

  return (
    <Box className={classes.container}>
      <div className={classes.grid}>
        <Typography
          id={simpleId}
          data-testid="difficulty-simple"
          component="label"
          htmlFor={switchId}
          className={classNames(classes.label, classes.left, isComplex && classes.labelDimmed)}
          variant="subtitle1"
        >
          {t('home.input.dialog.difficulty.simple')}

          <Typography className={classes.hint} variant="body2">
            {t('home.input.dialog.difficulty.simple_hint')}
          </Typography>
        </Typography>

        <Switch
          id={switchId}
          checked={isComplex}
          onChange={handleToggle}
          inputProps={{
            'aria-labelledby': `${simpleId} ${complexId}`,
            'aria-describedby': `${hintSimpleId} ${hintComplexId}`,
            name: 'difficulty',
          }}
          classes={{
            root: classes.switchRoot,
            switchBase: classes.switchBase,
            thumb: classes.thumb,
            track: classes.track,
          }}
        />

        <Typography
          id={complexId}
          data-testid="difficulty-complex"
          component="label"
          htmlFor={switchId}
          className={classNames(classes.label, classes.right, !isComplex && classes.labelDimmed)}
          variant="subtitle1"
        >
          {t('home.input.dialog.difficulty.complex')}

          <Typography id={hintComplexId} className={classes.hint} variant="body2">
            {t('home.input.dialog.difficulty.complex_hint')}
          </Typography>
        </Typography>

        <span />
      </div>

      <div className={classes.next}>
        <Button id="btn_difficulty_next" variant="contained" color="primary" onClick={handleNext}>
          {t('home.input.dialog.next')}
        </Button>
      </div>
    </Box>
  );
};
export default Difficulty;
